import { useState, useEffect } from "react";
import { API_ENDPOINTS } from "@/constants/constants";
import { RefreshCw, Search, ArrowUpDown, Calendar } from "lucide-react";

// Interface pour les messages
interface Message {
  id: string;
  email: string;
  tel?: string;
  message: string;
  createdAt: string;
  read?: boolean;
}

// Clé de stockage pour localStorage (utilisé comme solution de secours)
const READ_MESSAGES_STORAGE_KEY = "admin_read_messages";

const AdminMessages = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updatingMessageId, setUpdatingMessageId] = useState<string | null>(
    null
  );

  // Initialiser avec les valeurs du localStorage (comme solution de secours)
  const [viewedMessageIds, setViewedMessageIds] = useState<Set<string>>(() => {
    try {
      const storedIds = localStorage.getItem(READ_MESSAGES_STORAGE_KEY);
      return storedIds ? new Set(JSON.parse(storedIds)) : new Set();
    } catch (error) {
      console.error("Erreur lors de la récupération des messages lus:", error);
      return new Set();
    }
  });

  // Filtres et tri
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc"); // par défaut du plus récent au plus ancien
  const [emailFilter, setEmailFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchMessages();
  }, []);

  // Persister les messages lus dans localStorage comme solution de secours
  useEffect(() => {
    try {
      localStorage.setItem(
        READ_MESSAGES_STORAGE_KEY,
        JSON.stringify([...viewedMessageIds])
      );
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des messages lus:", error);
    }
  }, [viewedMessageIds]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.CONTACT.GET_MESSAGES, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Erreur lors de la récupération des messages: ${response.status}`
        );
      }

      const data = await response.json();
      setMessages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour mettre à jour le statut de lecture via l'API
  const markMessageAsRead = async (messageId: string) => {
    if (updatingMessageId === messageId) return; // Éviter les requêtes en double

    setUpdatingMessageId(messageId);

    try {
      // Utilisation de l'endpoint spécifique pour marquer comme lu
      const response = await fetch(
        `${API_ENDPOINTS.CONTACT.MARK_AS_READ(messageId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.ok) {
        // Mettre à jour l'état des messages
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === messageId ? { ...msg, read: true } : msg
          )
        );

        // Ajouter également au localStorage comme solution de secours
        setViewedMessageIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(messageId);
          return newSet;
        });

        console.log(`Message ${messageId} marqué comme lu avec succès via API`);
      } else {
        throw new Error(`Erreur lors de la mise à jour: ${response.status}`);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);

      // En cas d'échec de l'API, utiliser le localStorage comme solution de secours
      setViewedMessageIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(messageId);
        return newSet;
      });
    } finally {
      setUpdatingMessageId(null);
    }
  };

  // Fonction pour marquer un message comme non lu
  const handleMessageClick = async (message: Message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);

    // Marquer comme lu si ce n'est pas déjà le cas
    if (!message.read) {
      await markMessageAsRead(message.id);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  // Détermine si un message doit être affiché comme lu
  const isMessageRead = (messageId: string) => {
    // 1. Vérifier d'abord si le message est marqué comme lu dans les données de l'API
    const message = messages.find((m) => m.id === messageId);
    if (message?.read) return true;

    // 2. Si non, vérifier dans le localStorage comme solution de secours
    return viewedMessageIds.has(messageId);
  };

  // Fonction pour trier les messages
  const toggleSortDirection = () => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  // Filtrer et trier les messages
  const filteredAndSortedMessages = messages
    .filter((message) => {
      // Filtre par email
      if (
        emailFilter &&
        !message.email.toLowerCase().includes(emailFilter.toLowerCase())
      ) {
        return false;
      }

      // Filtre par plage de dates
      if (startDate) {
        const messageDate = new Date(message.createdAt);
        const filterStartDate = new Date(startDate);
        // Définir l'heure à minuit pour comparer les dates correctement
        filterStartDate.setHours(0, 0, 0, 0);
        if (messageDate < filterStartDate) {
          return false;
        }
      }

      if (endDate) {
        const messageDate = new Date(message.createdAt);
        const filterEndDate = new Date(endDate);
        // Définir l'heure à 23:59:59 pour inclure tous les messages de cette journée
        filterEndDate.setHours(23, 59, 59, 999);
        if (messageDate > filterEndDate) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      // Tri par date
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
    });

  // Réinitialiser les filtres
  const resetFilters = () => {
    setEmailFilter("");
    setStartDate("");
    setEndDate("");
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Messages</h1>
      <p className="mt-4">Gérez les messages de la plateforme.</p>
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Boîte de réception</h2>
          <button
            onClick={fetchMessages}
            className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>

        {/* Filtres */}
        <div className="mb-6 space-y-3">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Filtre par email */}
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="filter-email" className="block text-sm font-medium text-gray-700 mb-1">
                Rechercher par email
              </label>
              <div className="relative">
                <input
                  id="filter-email"
                  type="text"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  placeholder="Entrez un email..."
                  className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Filtre par date - début */}
            <div>
              <label htmlFor="filter-start-date" className="block text-sm font-medium text-gray-700 mb-1">
                Date de début
              </label>
              <input
                id="filter-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Filtre par date - fin */}
            <div>
              <label htmlFor="filter-end-date" className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin
              </label>
              <input
                id="filter-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Boutons */}
            <div className="flex gap-2">
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                disabled={!emailFilter && !startDate && !endDate}
              >
                Réinitialiser
              </button>
              <button
                onClick={toggleSortDirection}
                className="flex items-center gap-1 px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
              >
                <ArrowUpDown size={16} />
                {sortDirection === "desc"
                  ? "Plus récent d'abord"
                  : "Plus ancien d'abord"}
              </button>
            </div>
          </div>

          {/* Informations sur les filtres actifs */}
          {(emailFilter || startDate || endDate) && (
            <div className="text-sm text-gray-500 flex flex-wrap gap-2 items-center">
              <span>Filtres actifs:</span>
              {emailFilter && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Email: {emailFilter}
                </span>
              )}
              {(startDate || endDate) && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                  <Calendar size={14} className="mr-1" />
                  {startDate &&
                    `Du ${new Date(startDate).toLocaleDateString("fr-FR")}`}
                  {startDate && endDate && " "}
                  {endDate &&
                    `au ${new Date(endDate).toLocaleDateString("fr-FR")}`}
                </span>
              )}
            </div>
          )}
        </div>

        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && filteredAndSortedMessages.length === 0 && (
          <p>Aucun message trouvé pour les critères sélectionnés.</p>
        )}

        {!loading && !error && filteredAndSortedMessages.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Email
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Téléphone
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Message
                  </th>
                  <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedMessages.map((message) => {
                  const messageIsRead = isMessageRead(message.id);
                  const isUpdating = updatingMessageId === message.id;
                  return (
                    <tr
                      key={message.id}
                      className={`hover:bg-gray-50 cursor-pointer ${
                        messageIsRead ? "" : "bg-gray-50"
                      } ${isUpdating ? "opacity-80" : ""}`}
                      onClick={() => handleMessageClick(message)}
                    >
                      <td
                        className={`py-3 px-4 border-b ${
                          !messageIsRead && "font-bold"
                        }`}
                      >
                        {message.email}
                      </td>
                      <td
                        className={`py-3 px-4 border-b ${
                          !messageIsRead && "font-bold"
                        }`}
                      >
                        {message.tel || "-"}
                      </td>
                      <td
                        className={`py-3 px-4 border-b whitespace-pre-wrap ${
                          !messageIsRead && "font-bold"
                        }`}
                      >
                        {message.message.length > 100
                          ? message.message.substring(0, 100) + "..."
                          : message.message}
                      </td>
                      <td
                        className={`py-3 px-4 border-b ${
                          !messageIsRead && "font-bold"
                        }`}
                      >
                        {new Date(message.createdAt).toLocaleDateString(
                          "fr-FR",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal pour afficher le message complet */}
      {isModalOpen && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Détails du message</h3>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-500">De</p>
                <p className="font-medium">{selectedMessage.email}</p>
              </div>

              {selectedMessage.tel && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Téléphone</p>
                  <p>{selectedMessage.tel}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-500">Reçu le</p>
                <p>
                  {new Date(selectedMessage.createdAt).toLocaleDateString(
                    "fr-FR",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Message
                </p>
                <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                  {selectedMessage.message}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
